/* Predictive search (§8.3). Debounced 250ms → Section Rendering API → results listbox.
   The input is an ARIA combobox: ↑/↓ move aria-activedescendant, Enter opens the selected option. */
class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-results]');
    this.status = this.querySelector('[data-status]');
    this.cache = new Map();
    this.onInput = window.Theme.debounce(() => this.search(this.input.value.trim()), 250);
    this.onKeydown = (event) => this.handleKey(event);
    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    if (this.controller) this.controller.abort();
  }

  url(term) {
    const { routes } = window.Theme.config;
    const params = new URLSearchParams({
      q: term,
      'resources[type]': this.dataset.types || 'product',
      'resources[limit]': '6',
      'resources[options][unavailable_products]': 'last',
      section_id: 'predictive-search',
    });
    return `${routes.predictiveSearch}?${params}`;
  }

  async search(term) {
    if (!term) return this.clear();
    const url = this.url(term);
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();
    try {
      let html = this.cache.get(url);
      if (!html) {
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error(String(response.status));
        html = await response.text();
        this.cache.set(url, html);
      }
      const content = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-predictive-results]');
      if (!content) return this.clear();
      this.results.innerHTML = content.innerHTML;
      this.status.textContent = content.dataset.status || '';
      this.input.setAttribute('aria-expanded', String(Boolean(this.results.querySelector('[role="listbox"]'))));
      this.input.removeAttribute('aria-activedescendant');
    } catch (error) {
      if (error.name !== 'AbortError') this.clear();
    }
  }

  clear() {
    this.results.innerHTML = '';
    this.status.textContent = '';
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
  }

  options() {
    return [...this.results.querySelectorAll('[role="option"]')];
  }

  handleKey(event) {
    const options = this.options();
    if (!options.length) return;
    const current = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const next = (current + step + options.length) % options.length;
      options.forEach((option, i) => option.setAttribute('aria-selected', String(i === next)));
      this.input.setAttribute('aria-activedescendant', options[next].id);
      options[next].scrollIntoView({ block: 'nearest' });
    } else if (event.key === 'Enter' && current > -1) {
      const link = options[current].querySelector('a');
      if (link) {
        event.preventDefault();
        link.click();
      }
    }
  }
}

if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
